import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { cn } from '@april9au/react-common/utils';

/**
 * The Stack9 forms embed is a remote script that registers a global and renders
 * into an element by id — there is no npm package and no React API. This is the
 * only place in the app that touches it.
 *
 * Both consumers — `/contact` and the passport form on `/get-passport` — load
 * the same script from the same URL, so the load is shared: `loadScript`
 * memoises one promise per URL and every widget awaits it.
 */
declare global {
  interface Window {
    Stack9Form?: {
      create: (options: {
        formUrl: string;
        targetElementId: string;
        height: string;
        injectFormValues: Record<string, unknown>;
        onComplete?: (payload: unknown) => void;
      }) => void;
    };
  }
}

const loads = new Map<string, Promise<void>>();

/** Injects the embed script once per URL and resolves when the global is ready. */
function loadScript(src: string): Promise<void> {
  const existing = loads.get(src);

  if (existing) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');

    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      // Drop the rejected promise so a remount (or a retry) can try again.
      loads.delete(src);
      reject(new Error(`Failed to load ${src}`));
    });

    document.head.appendChild(script);
  });

  loads.set(src, promise);

  return promise;
}

/**
 * The one size message the form sends. `s9forms` posts it through the embed's
 * iframily channel once, when the iframe pairs, and the embed script itself
 * ignores it — so the widget listens for it directly.
 */
const iframeHeightMessage = z.object({
  _fromType: z.literal('child'),
  msg: z.object({ action: z.literal('SET_IFRAME_HEIGHT'), payload: z.number().positive() }),
});

/**
 * Room for the validation messages SurveyJS adds under each required field on
 * a failed submit. The form reports its height once and never again, so
 * without this a rejected submit would push the button under an inner
 * scrollbar.
 */
const VALIDATION_ALLOWANCE_PX = 160;

/** How long to wait for the size message before falling back to `height`. */
const FIT_TIMEOUT_MS = 6000;

export type S9FormWidgetProps = {
  /** The form's Stack9 URL. Empty or absent renders `fallback` instead. */
  formUrl: string | undefined;
  /** The embed script, `${APP_S9_FORM_SERVICE_BASE_URL}/scripts/embed-form/…`. */
  scriptUrl: string | undefined;
  /** Unique per widget on a page; the script renders into this element. */
  targetElementId: string;
  /**
   * CSS height reserved while the form loads, and the iframe's height if the
   * form never reports its own. Close to the real height keeps the shift small.
   */
  height: string;
  injectFormValues?: Record<string, unknown>;
  /** The submitted payload, unparsed — the caller owns its schema. */
  onComplete?: (payload: unknown) => void;
  /** Shown when the form is not configured, and when the script fails to load. */
  fallback: React.ReactNode;
  /** Called when the script cannot be loaded, in addition to rendering `fallback`. */
  onError?: () => void;
  className?: string;
};

/**
 * Renders a Stack9 form, or `fallback` when the form is not configured (form
 * URLs come from `APP_SECRETS` and are blank locally) or the script fails.
 *
 * The iframe is sized to its content, so the page scrolls and the form never
 * does. It is created 1 px tall because the form measures itself as
 * `max(content, viewport)`: at any larger height it would only echo that height
 * back. The first report sets the height (plus `VALIDATION_ALLOWANCE_PX`) and
 * later ones are ignored — each would be at least the height just set, so
 * honouring them would only ever grow the frame.
 */
export function S9FormWidget({
  formUrl,
  scriptUrl,
  targetElementId,
  height,
  injectFormValues = {},
  onComplete,
  fallback,
  onError,
  className,
}: S9FormWidgetProps) {
  const configured = Boolean(formUrl && scriptUrl);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!formUrl || !scriptUrl) {
      return;
    }

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const formOrigin = new URL(formUrl).origin;
    const iframe = () => host.current?.querySelector('iframe') ?? null;

    const fit = (frameHeight: string) => {
      const frame = iframe();

      if (!frame || cancelled) {
        return;
      }

      frame.style.height = frameHeight;
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      setStatus('ready');
    };

    function onMessage(event: MessageEvent) {
      const frame = iframe();

      if (event.origin !== formOrigin || !frame || event.source !== frame.contentWindow) {
        return;
      }

      const parsed = iframeHeightMessage.safeParse(event.data);

      if (parsed.success) {
        fit(`${Math.ceil(parsed.data.msg.payload) + VALIDATION_ALLOWANCE_PX}px`);
      }
    }

    // Listening before `create`, so the report cannot arrive unheard.
    window.addEventListener('message', onMessage);

    loadScript(scriptUrl)
      .then(() => {
        if (cancelled) {
          return;
        }

        // `create` is only callable once the script has registered the global.
        if (!window.Stack9Form) {
          throw new Error('Stack9Form global is missing after the script loaded');
        }

        window.Stack9Form.create({ formUrl, targetElementId, height: '1px', injectFormValues, onComplete });
        iframe()?.style.setProperty('display', 'block');
        // A form build that never reports its height still gets a usable frame.
        timeout = setTimeout(() => fit(height), FIT_TIMEOUT_MS);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        console.error('[s9-form-widget]', error);
        setStatus('failed');
        onError?.();
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
    };
    // `injectFormValues` and `onComplete` are captured on the first render on
    // purpose: the widget is created once, and re-creating it would throw away
    // whatever the visitor has already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formUrl, scriptUrl, targetElementId, height]);

  if (!configured || status === 'failed') {
    return (
      <div
        role="status"
        className={cn(
          'border-surf-gray text-surf-dark-blue rounded-2xl border-2 border-dashed bg-white/60 p-5 text-sm',
          className,
        )}
      >
        {fallback}
      </div>
    );
  }

  // `height` is reserved while the form loads, and the placeholder rows sit
  // inside it — they occupy no layout of their own — so "a form is coming"
  // shows instead of a blank slab. Once the iframe is sized it sets the height
  // itself and the reservation is dropped.
  return (
    <div
      ref={host}
      className={cn('relative', className)}
      style={status === 'loading' ? { minHeight: height } : undefined}
    >
      <div id={targetElementId} />

      {status === 'loading' ? (
        <div aria-hidden className="absolute inset-x-0 top-0 space-y-3">
          <div className="bg-surf-gray h-12 rounded-xl" />
          <div className="bg-surf-gray h-12 rounded-xl" />
          <div className="bg-surf-gray h-12 w-2/3 rounded-xl" />
        </div>
      ) : null}
    </div>
  );
}
